import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectCoverflow, Navigation, Pagination } from 'swiper/modules';
import SEO from '../components/SEO';
import AnnouncementBanner from '../components/AnnouncementBanner';
import ContactForm from '../components/ContactForm';
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const defaultBlogPosts = [
  {
    slug: "dubai-str-market-2026",
    image: "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800",
    category: "Market Insights",
    title: "Dubai's STR Market in 2026: What Property Owners Need to Know",
    excerpt: "Latest regulations, occupancy trends, and emerging neighborhoods driving UAE's vacation rental boom.",
    metaTitle: "Dubai STR Market 2026: What Property Owners Need to Know",
    metaDescription: "Dubai's short-term rental market is shifting — and smart operators are already positioning for the rebound. Here's what the 2026 numbers mean for your property.",
    content: `Dubai's short-term rental market continues to evolve rapidly, and 2026 is shaping up to be a pivotal year for property owners and operators across the UAE.

The Market Is Maturing

What we're seeing across Dubai, Abu Dhabi, and Ras Al Khaimah is a market that's moving away from the "list it and they will come" mentality. Occupancy rates are stabilizing, competition is increasing, and guests are becoming more discerning about where they stay.

This isn't bad news — it's a natural market correction that rewards operators who invest in quality, strategy, and guest experience.

Regulatory Landscape

Dubai Tourism's continued rollout of STR regulations means compliance is no longer optional. Properties must be properly licensed, and operators who haven't registered their units are finding themselves shut out of major OTAs.

The good news? Compliance creates a level playing field. Properties that were competing on price alone are being replaced by legitimate operators who understand hospitality as a business.

Emerging Neighborhoods

While Dubai Marina and Downtown remain strong performers, we're seeing increased interest in:
- Jumeirah Village Circle and Triangle
- Dubai Hills Estate
- Business Bay (particularly for corporate stays)
- Ras Al Khaimah beachfront properties

These areas offer better value per square meter and are attracting both investors and guests looking for alternatives to the premium districts.

What This Means for Operators

The operators thriving in 2026 are those who:
- Treat their STR as a business, not a side project
- Invest in professional listing optimization and photography
- Implement dynamic pricing strategies
- Focus on guest experience from pre-booking to checkout
- Stay on top of platform algorithm changes

If you're still using static pricing, recycled listing copy, or treating reviews as an afterthought, 2026 is the year to level up.

The Opportunity

Dubai's tourism sector is projected to continue growing, with visitor numbers expected to reach new highs. The opportunity is real — but it's no longer enough to simply own a property in a good location.

Success now belongs to operators who understand that short-term rentals are a hospitality business that happens to take place in residential property.

What Should You Do?

If you're serious about maximizing your property's performance in 2026:
1. Get a performance audit to understand where you stand
2. Optimize your listing for platform algorithms and guest conversion
3. Implement proper revenue management (not just calendar-based pricing)
4. Design a guest experience that drives 5-star reviews
5. Stay compliant with local regulations

The UAE STR market is evolving from Wild West to mature industry. The operators who adapt will thrive. Those who don't will wonder why their bookings are down.`,
    author: "Stephanie Williams",
    published_date: new Date().toISOString(),
    read_time: "5 min read"
  },
  {
    slug: "5-star-guest-experience",
    image: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800",
    category: "Operations",
    title: "The 5-Star Guest Experience Blueprint",
    excerpt: "How top-performing UAE properties consistently deliver exceptional stays that drive repeat bookings.",
    content: `Every operator wants 5-star reviews. But most approach it backwards — they focus on the property itself rather than the guest journey.

Here's the truth: a perfectly designed apartment with high-end finishes can still get 3-star reviews if the guest experience is broken. And a modest property with an exceptional guest journey will consistently outperform it.

The UAE's top STR operators understand this. Here's how they do it.

It Starts Before They Arrive

The guest experience doesn't begin at check-in — it begins the moment someone views your listing.

Top performers ensure:
- Professional, high-quality photography that sets accurate expectations
- Listing copy that answers questions before they're asked
- Clear, honest descriptions of the space, location, and amenities
- Pre-booking communication that builds confidence

Once someone books, the experience intensifies. You have one chance to set the tone, and it happens between booking confirmation and arrival.

What to send:
- Personalized welcome message within 2 hours of booking
- Clear check-in instructions (sent 48 hours before arrival)
- Local recommendations tailored to their stay purpose
- Proactive communication about anything that might affect their stay

The 48 Hours Before Check-In

This is where most operators drop the ball. They assume the guest remembers the check-in instructions from the booking confirmation. They don't.

48 hours before arrival, send:
- Exact check-in location with map link
- Access codes or key collection details
- Parking instructions if applicable
- What to do if they arrive early or late
- Emergency contact details

Check-In Experience

The moment a guest unlocks your door, they're forming their impression of the stay. You have about 60 seconds to deliver on the promise your listing made.

What guests notice immediately:
- Temperature (too hot, too cold, or stuffy kills the vibe)
- Smell (this is make-or-break)
- Cleanliness (not clean enough vs hospitality-grade clean)
- First visual impression (does it match the photos?)

Top properties include:
- A welcome note (handwritten beats printed)
- A small welcome amenity (dates, Arabic coffee, local treats)
- Crystal-clear house manual (not a 10-page essay)
- Emergency contact information displayed prominently

During The Stay

The best guest experience is one where guests don't need to contact you — because everything works and everything they might need is already there.

But when they do reach out, response time matters. A lot.

In the UAE market:
- Respond within 15 minutes during waking hours
- Have a backup contact available 24/7
- Solve problems proactively (don't wait for complaints)
- Follow up without being intrusive

The Check-Out Experience

Most operators think check-out is just about getting the keys back. Top performers see it as the moment that determines whether someone leaves a review and what they say.

Make check-out easy:
- Flexible check-out time when possible (builds goodwill)
- Simple departure instructions
- A thank you message
- No aggressive cleaning demands

Then, after they leave:
- Send a thank you message within 24 hours
- Politely request a review (without begging)
- Invite them to return

What Drives Repeat Bookings

Repeat guests are gold — they book directly, they're lower risk, and they spend more per stay.

What makes guests come back:
- Consistency (the experience matches what they remember)
- Personal touches (remembering their preferences)
- Feeling valued (not just processed)
- Reliability (everything works, every time)

The Bottom Line

Five-star reviews don't come from five-star fixtures. They come from a guest journey that makes someone feel like they made the right choice.

Dubai's best STR operators obsess over this. The ones struggling are still focused on marble countertops.

If your reviews aren't where you want them to be, audit your guest journey. The answer is there.`,
    author: "Stephanie Williams",
    published_date: new Date().toISOString(),
    read_time: "7 min read"
  },
  {
    slug: "dynamic-pricing-strategies",
    image: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800",
    category: "Revenue Strategy",
    title: "Dynamic Pricing Strategies That Actually Work",
    excerpt: "Real-world pricing tactics from Dubai's most profitable short-term rental operators.",
    metaTitle: "Should I Hire a Dynamic Pricing Consultant for My Dubai STR?",
    metaDescription: "Dynamic pricing tools only work if your listing is already optimised. Here's what Dubai STR operators need to know before investing in pricing software — and what to do first.",
    content: `Every STR operator knows they should be using dynamic pricing. Most are doing it wrong.

The problem isn't the concept — it's the execution. Operators buy a tool, connect it to their calendar, and assume the algorithm will handle everything. Then they wonder why their revenue hasn't improved.

Here's what actually works.

Dynamic Pricing Is Not a Silver Bullet

Let's be clear: dynamic pricing software is useful. But it's only valuable if your fundamentals are right.

If your listing:
- Has poor photography
- Ranks low in search results
- Converts poorly because of weak copy
- Has mediocre reviews

...then optimizing your price won't help. You're just adjusting the cost of something nobody wants to book.

The operators seeing real results from dynamic pricing already have:
- Strong listing performance (high search ranking)
- Professional presentation (photos, copy, amenities)
- Solid reviews (4.7+ average)
- Accurate calendar management

Only then does pricing optimization move the needle.

What Dynamic Pricing Actually Does

At its core, dynamic pricing adjusts your rates based on:
- Demand forecasts (events, holidays, seasonality)
- Competitor pricing in your area
- Your booking velocity (how fast your dates are filling)
- Historical occupancy patterns

The goal is to maximize revenue — not bookings. That's a critical distinction.

A tool might recommend raising your price during low season because your property is desirable enough to still book at a premium. Or it might lower your price during high season if you're priced out of the market.

Manual pricing can't keep up with this. Dynamic pricing can.

But only if you're using it right.

The Mistakes Operators Make

Mistake #1: Over-optimizing for occupancy

Occupancy rate is a vanity metric. What matters is RevPAR (revenue per available room). A property at 60% occupancy with a higher nightly rate can outperform one at 90% occupancy with rock-bottom pricing.

Mistake #2: Trusting the algorithm blindly

Dynamic pricing tools use data, but they don't know your property like you do. If there's construction next door, a lift outage, or a quality issue, the algorithm doesn't account for that.

You still need to manually override pricing when circumstances change.

Mistake #3: Not setting proper floors and ceilings

If you let the tool price too low, you devalue your property and attract the wrong guests. If you let it price too high, you'll sit empty while competitors book out.

Set realistic boundaries:
- Minimum nightly rate (below this, it's not worth your costs)
- Maximum nightly rate (above this, you're priced out of your segment)

Mistake #4: Ignoring your competition

Dynamic pricing adjusts based on market rates. But if your competitors are also using dynamic pricing tools, you end up in a race to the bottom.

The solution? Differentiate on experience, not price.

The Real Strategy

The most successful operators in Dubai don't just use dynamic pricing — they layer it into a broader revenue strategy.

Here's what they do:

1. Optimize for search ranking first
Before worrying about price, get your listing visible. Ranking on page 1 of search results is more valuable than perfect pricing on page 3.

2. Segment your pricing
Not every night has the same value. Weekends, holidays, events, and corporate booking seasons all behave differently. Tailor your pricing strategy by segment.

3. Incentivize longer stays
Offer discounts for weekly and monthly bookings. These stabilize cash flow and reduce turnover costs.

4. Test and refine
Dynamic pricing tools give you data. Use it. Track which price points drive bookings vs. which ones don't convert. Adjust your strategy accordingly.

5. Stay competitive without undercutting
Know what comparable properties charge. Price below them if you're newer or have fewer reviews. Match them once you're established. Price above them only if you genuinely offer more value.

When to Override the Algorithm

Even with a tool managing your pricing, there are times to step in:
- You have a maintenance issue affecting guest experience
- A major event happens that the algorithm hasn't priced for yet
- Your reviews dip and you need to rebuild trust
- You're testing a new strategy (e.g., attracting longer stays)

Good operators don't set-and-forget. They monitor, adjust, and iterate.

Should You Invest in a Tool?

If your property is:
- Well-presented
- Ranking well in search
- Getting consistent bookings
- Generating solid reviews

...then yes, dynamic pricing software is worth it. It'll save you time and likely increase revenue by 10-20%.

But if your fundamentals aren't right, fix those first. The best pricing strategy in the world can't save a poorly optimized listing.

The Bottom Line

Dynamic pricing works — but only as part of a complete revenue strategy. The operators making serious money aren't just using tools. They're combining smart pricing with strong listings, great guest experiences, and relentless optimization.

If you're serious about maximizing revenue, start with the fundamentals. Then layer in dynamic pricing once you've built something worth pricing dynamically.`,
    author: "Stephanie Williams",
    published_date: new Date().toISOString(),
    read_time: "6 min read"
  }
];

const Home = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [showCalendly, setShowCalendly] = useState(false);
  const [blogPosts, setBlogPosts] = useState(defaultBlogPosts);

  const fetchBlogPosts = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/blog/posts`);
      if (response.ok) {
        const data = await response.json();
        // Only update if API returns posts, otherwise keep defaults
        if (data && data.length > 0) {
          setBlogPosts(data.slice(0, 3)); // Show only 3 latest posts
        }
        // If data is empty, keep the default posts (don't update state)
      } else {
        // Fallback to default posts if API fails
        setBlogPosts(defaultBlogPosts);
      }
    } catch (error) {
      console.error('Failed to fetch blog posts:', error);
      // Use default posts as fallback
      setBlogPosts(defaultBlogPosts);
    }
  }, []);

  useEffect(() => {
    // Load Calendly script
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    script.onload = () => {
      console.log('Calendly script loaded successfully');
    };
    document.body.appendChild(script);
    
    // Add Calendly styles
    const link = document.createElement('link');
    link.href = 'https://assets.calendly.com/assets/external/widget.css';
    link.rel = 'stylesheet';
    document.head.appendChild(link);

    // Fetch blog posts
    fetchBlogPosts();

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
      if (document.head.contains(link)) {
        document.head.removeChild(link);
      }
    };
  }, [fetchBlogPosts]);
  
  const processSteps = [
    {
      num: "01",
      title: "Discovery",
      desc: "Understand your property, your goals, and your current gaps"
    },
    {
      num: "02",
      title: "Audit",
      desc: "Data, listings, reviews, and the real guest experience"
    },
    {
      num: "03",
      title: "Roadmap",
      desc: "Prioritised action plan — practical, not theoretical"
    },
    {
      num: "04",
      title: "Implement",
      desc: "We work alongside you to execute every change"
    },
    {
      num: "05",
      title: "Optimise",
      desc: "Track results, measure what changed, and refine"
    }
  ];

  const nextStep = () => {
    setCurrentStep((prev) => (prev + 1) % processSteps.length);
  };

  const prevStep = () => {
    setCurrentStep((prev) => (prev - 1 + processSteps.length) % processSteps.length);
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: newsletterEmail }),
      });

      const data = await response.json();

      if (response.ok) {
        if (data.status === 'already_subscribed') {
          alert('You are already subscribed to our newsletter!');
        } else {
          alert('Thank you for subscribing! You\'ll receive our latest insights and updates.');
        }
        setNewsletterEmail('');
      } else {
        alert('Failed to subscribe. Please try again.');
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      alert('Failed to subscribe. Please try again later.');
    }
  };

  const [currentServiceIndex, setCurrentServiceIndex] = useState(0);

  const scrollServicesCarousel = (direction) => {
    setCurrentServiceIndex(prev => {
      if (direction === 'next') {
        return (prev + 1) % 7;
      } else {
        return prev === 0 ? 6 : prev - 1;
      }
    });
  };

  const scrollExpertiseCarousel = (direction) => {
    const carousel = document.querySelector('.expertise-carousel');
    if (carousel) {
      const scrollAmount = 404; // card width (380) + gap (24)
      carousel.scrollBy({
        left: direction === 'next' ? scrollAmount : -scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const openCalendly = (e) => {
    if (e) e.preventDefault();
    
    // Try to use Calendly's popup widget
    if (typeof window !== 'undefined' && window.Calendly) {
      try {
        window.Calendly.initPopupWidget({
          url: 'https://calendly.com/stephanieluxesolutions/30min?hide_gdpr_banner=1&primary_color=ff6500'
        });
        return false;
      } catch (error) {
        console.error('Calendly popup error:', error);
      }
    }
    
    // Fallback: open Calendly in new tab
    window.open('https://calendly.com/stephanieluxesolutions/30min', '_blank');
    return false;
  };

  const closeCalendly = () => {
    setShowCalendly(false);
    document.body.style.overflow = 'auto';
  };

  return (
    <>
      <SEO 
        title="UAE Short-Term Rental Consultancy"
        description="Stephanies Luxe Solutions is the UAE's detail-first STR consultancy helping property investors and operators build high-performing short-term rentals through listing optimisation, pricing strategy, guest experience design, and mystery shopping."
        canonical="https://stephaniesluxesolutions.com/"
      />
      <style>{`
        :root {
          --espresso: #2C1F14;
          --espresso-deep: #221508;
          --espresso-mid: #4A3728;
          --mocha: #6B5744;
          --warm-gold: #C4A882;
          --gold-light: #D4BCA0;
          --cream: #F5F0E8;
          --sand: #EDE5D8;
          --warm-stone: #DDD4C0;
          --stone-mid: #C8BEA8;
        }

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        html { scroll-behavior: smooth; }

        body {
          font-family: 'DM Sans', sans-serif;
          background: var(--cream);
          color: var(--espresso);
          -webkit-font-smoothing: antialiased;
        }

        /* ── NAVIGATION ── */
        nav {
          position: sticky;
          top: 0;
          z-index: 100;
          background: var(--cream);
          border-bottom: 0.5px solid var(--warm-stone);
          padding: 16px 48px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .nav-brand {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 3px;
          text-decoration: none;
        }

        .nav-monogram {
          font-family: 'Cormorant Garamond', serif;
          font-size: 22px;
          font-weight: 400;
          color: var(--espresso);
          letter-spacing: 0.04em;
          display: flex;
          align-items: center;
        }

        .nav-monogram .fade { opacity: 0.2; }

        .nav-wordmark {
          font-size: 7px;
          letter-spacing: 0.26em;
          text-transform: uppercase;
          color: var(--mocha);
          font-weight: 400;
        }

        .nav-links {
          display: flex;
          gap: 28px;
          align-items: center;
        }

        .nav-links a {
          font-size: 10.5px;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: var(--mocha);
          text-decoration: none;
          transition: color 0.2s;
        }

        .nav-links a:hover { color: var(--espresso); }

        .nav-cta {
          font-size: 10px;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          border: 0.5px solid var(--espresso);
          color: var(--espresso);
          padding: 9px 20px;
          text-decoration: none;
          transition: background 0.2s, color 0.2s;
        }

        .nav-cta:hover {
          background: var(--espresso);
          color: var(--cream);
        }

        /* ── HERO ── */
        .hero {
          position: relative;
          padding: 96px 48px 88px;
          text-align: center;
          overflow: hidden;
          min-height: 75vh;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .hero-video-bg {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          z-index: 0;
          pointer-events: none;
        }

        .hero-video-bg::-webkit-media-controls {
          display: none !important;
        }

        .hero-video-bg::-webkit-media-controls-enclosure {
          display: none !important;
        }

        .hero-video-bg::-webkit-media-controls-panel {
          display: none !important;
        }

        .hero-video-bg::-webkit-media-controls-play-button {
          display: none !important;
        }

        .hero-video-bg::-webkit-media-controls-start-playback-button {
          display: none !important;
        }

        .hero-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(to bottom, rgba(245, 240, 232, 0.75), rgba(245, 240, 232, 0.80));
          z-index: 1;
        }

        .hero-content {
          position: relative;
          z-index: 2;
          max-width: 100%;
        }

        .hero .eyebrow {
          color: var(--warm-gold);
          text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
          font-weight: 500;
        }

        .hero h1 {
          color: var(--espresso-deep);
          text-shadow: 0 2px 8px rgba(255, 255, 255, 0.8);
          font-weight: 400;
        }

        .hero-sub {
          color: var(--espresso-deep);
          font-weight: 500;
          text-shadow: 0 1px 6px rgba(255, 255, 255, 0.9);
        }

        .eyebrow {
          font-size: 10px;
          letter-spacing: 0.24em;
          text-transform: uppercase;
          color: var(--warm-gold);
          font-weight: 400;
          margin-bottom: 18px;
        }

        .gold-rule {
          width: 32px;
          height: 0.5px;
          background: var(--warm-gold);
          margin: 0 auto 22px;
        }

        .hero h1 {
          font-family: 'Cormorant Garamond', serif;
          font-size: 48px;
          font-weight: 300;
          line-height: 1.18;
          letter-spacing: 0.01em;
          color: var(--espresso);
          max-width: 640px;
          margin: 0 auto 22px;
        }

        .hero-sub {
          font-size: 15px;
          color: var(--espresso-deep);
          line-height: 1.8;
          max-width: 480px;
          margin: 0 auto 36px;
          font-weight: 500;
          text-shadow: 0 1px 6px rgba(255, 255, 255, 0.9);
        }

        .hero-actions {
          display: flex;
          gap: 20px;
          align-items: center;
          justify-content: center;
        }

        .btn-primary {
          font-size: 10px;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          background: var(--espresso);
          color: var(--cream);
          padding: 14px 32px;
          text-decoration: none;
          transition: background 0.2s;
          font-weight: 500;
        }

        .btn-primary:hover { background: var(--espresso-mid); }

        .btn-ghost {
          font-size: 10px;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--mocha);
          text-decoration: none;
          border-bottom: 0.5px solid var(--warm-gold);
          padding-bottom: 2px;
          transition: color 0.2s;
        }

        .btn-ghost:hover { color: var(--espresso); }

        /* ── STATS BAR ── */
        .stats-bar {
          background: var(--sand);
          border-top: 0.5px solid var(--warm-stone);
          border-bottom: 0.5px solid var(--warm-stone);
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
        }

        .stat-item {
          padding: 24px 20px;
          text-align: center;
        }

        .stat-item + .stat-item {
          border-left: 0.5px solid var(--warm-stone);
        }

        .stat-num {
          font-family: 'Cormorant Garamond', serif;
          font-size: 22px;
          font-weight: 400;
          color: var(--espresso);
        }

        .stat-label {
          font-size: 10px;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--mocha);
          margin-top: 4px;
        }

        /* ── SECTIONS ── */
        section { padding: 80px 48px; }

        .section-eyebrow {
          font-size: 10px;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: var(--warm-gold);
          text-align: center;
          margin-bottom: 14px;
        }

        .section-h2 {
          font-family: 'Cormorant Garamond', serif;
          font-size: 34px;
          font-weight: 300;
          color: var(--espresso);
          text-align: center;
          line-height: 1.25;
          margin-bottom: 12px;
        }

        .section-sub {
          font-size: 14px;
          color: var(--mocha);
          text-align: center;
          line-height: 1.8;
          max-width: 480px;
          margin: 0 auto 48px;
          font-weight: 300;
        }

        /* ── SERVICES SWIPER CAROUSEL ── */
        .services-section { 
          background: var(--cream);
          padding: 80px 0 120px;
          position: relative;
        }

        .services-swiper-container {
          width: 100%;
          max-width: 1600px;
          margin: 0 auto;
          padding: 60px 40px;
        }

        .services-swiper {
          padding-bottom: 50px !important;
        }

        .swiper-slide {
          opacity: 1 !important;
        }

        .svc-card {
          width: 320px;
          min-height: 380px;
          background: var(--sand);
          border: 0.5px solid var(--warm-stone);
          border-radius: 20px;
          padding: 32px 26px;
          transition: all 0.4s ease;
          cursor: pointer;
          opacity: 1 !important;
        }

        .swiper-slide-active .svc-card {
          background: var(--cream);
          border-color: var(--warm-gold);
        }

        .svc-card.featured {
          background: var(--espresso);
          border-color: var(--warm-gold);
        }

        .swiper-slide-active .svc-card.featured {
          background: #4A3728;
        }

        .svc-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 2px;
          background: linear-gradient(90deg, transparent, var(--warm-gold), transparent);
          transition: left 0.6s ease;
        }

        .svc-card:hover::before {
          left: 100%;
        }

        .svc-card:hover {
          box-shadow: 0 20px 40px rgba(44, 31, 20, 0.2);
          border-color: var(--warm-gold);
        }

        .svc-icon {
          font-size: 38px;
          margin-bottom: 12px;
          transition: all 0.3s ease;
          display: inline-block;
          font-weight: 300;
          color: var(--warm-gold);
          font-family: 'Cormorant Garamond', serif;
        }

        .svc-card:hover .svc-icon {
          transform: scale(1.1);
          color: var(--espresso);
        }

        .svc-card.featured .svc-icon {
          color: var(--cream);
          font-size: 42px;
        }

        .svc-card.featured:hover .svc-icon {
          color: var(--warm-gold);
        }

        .svc-num {
          font-size: 9px;
          letter-spacing: 0.14em;
          color: var(--warm-gold);
          margin-bottom: 10px;
          display: inline-block;
        }

        .svc-card.featured .svc-num {
          color: rgba(245, 240, 232, 0.6);
        }

        .svc-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 17px;
          font-weight: 400;
          color: var(--espresso);
          margin-bottom: 10px;
          line-height: 1.3;
        }

        .svc-title.light { 
          color: var(--cream);
          font-size: 18px;
        }

        .svc-desc {
          font-size: 12px;
          color: var(--mocha);
          line-height: 1.6;
          font-weight: 300;
        }

        .svc-card:hover .svc-desc {
          color: var(--espresso);
        }

        .svc-desc.light { 
          color: rgba(245, 240, 232, 0.8);
          font-size: 12.5px;
        }

        .svc-card.featured:hover .svc-desc.light {
          color: rgba(245, 240, 232, 0.95);
        }

        .svc-badge {
          display: inline-block;
          font-size: 9px;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          background: var(--warm-gold);
          color: var(--espresso);
          padding: 5px 12px;
          margin-top: 12px;
          border-radius: 20px;
          font-weight: 600;
        }

        .svc-card:hover .svc-badge {
          box-shadow: 0 4px 8px rgba(196, 168, 130, 0.3);
        }

        /* Swiper navigation */
        .swiper-button-next,
        .swiper-button-prev {
          color: var(--espresso);
          width: 50px;
          height: 50px;
          background: var(--cream);
          border-radius: 50%;
          border: 1.5px solid var(--espresso);
        }

        .swiper-button-next:after,
        .swiper-button-prev:after {
          font-size: 20px;
        }

        .swiper-button-next:hover,
        .swiper-button-prev:hover {
          background: var(--espresso);
          color: var(--cream);
        }

        .swiper-pagination-bullet {
          background: var(--warm-gold);
          opacity: 0.5;
        }

        .swiper-pagination-bullet-active {
          background: var(--warm-gold);
          opacity: 1;
        }
          color: var(--espresso);
          border-color: var(--warm-gold);
        }

        /* Staggered entrance animation */
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(40px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .svc-card {
          animation: slideInUp 0.6s ease-out forwards;
          opacity: 0;
        }

        .svc-card:nth-child(1) { animation-delay: 0.1s; }
        .svc-card:nth-child(2) { animation-delay: 0.2s; }
        .svc-card:nth-child(3) { animation-delay: 0.3s; }
        .svc-card:nth-child(4) { animation-delay: 0.4s; }
        .svc-card:nth-child(5) { animation-delay: 0.5s; }
        .svc-card:nth-child(6) { animation-delay: 0.6s; }
        .svc-card:nth-child(7) { animation-delay: 0.7s; }

        /* ── ABOUT ── */
        .about-section {
          background: var(--sand);
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 64px;
          align-items: center;
        }

        .about-visual {
          background: var(--sand);
          padding: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          overflow: hidden;
          position: relative;
          min-height: 500px;
        }

        .about-visual img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          position: absolute;
          top: 0;
          left: 0;
        }

        .about-mono {
          font-family: 'Cormorant Garamond', serif;
          font-size: 72px;
          font-weight: 300;
          color: var(--cream);
          line-height: 1;
          letter-spacing: -0.02em;
          position: relative;
          display: flex;
          align-items: center;
        }

        .about-mono .fade { opacity: 0.15; color: var(--cream); }

        .about-crossbar {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          display: flex;
          flex-direction: column;
          align-items: center;
          height: 130%;
        }

        .about-crossbar-line {
          width: 0.5px;
          flex: 1;
          background: var(--warm-gold);
        }

        .about-wordmark-full {
          font-size: 8.5px;
          letter-spacing: 0.28em;
          text-transform: uppercase;
          color: var(--warm-gold);
          margin-top: 8px;
          text-align: center;
        }

        .about-content .eyebrow { text-align: left; }

        .about-h {
          font-family: 'Cormorant Garamond', serif;
          font-size: 30px;
          font-weight: 300;
          color: var(--espresso);
          line-height: 1.28;
          margin-bottom: 16px;
        }

        .about-body {
          font-size: 14px;
          color: var(--mocha);
          line-height: 1.82;
          font-weight: 300;
          margin-bottom: 16px;
        }

        .about-values {
          display: flex;
          flex-direction: column;
          gap: 10px;
          margin-top: 24px;
        }

        .about-value {
          display: flex;
          gap: 12px;
          align-items: flex-start;
        }

        .value-dot {
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: var(--warm-gold);
          flex-shrink: 0;
          margin-top: 7px;
        }

        .value-text {
          font-size: 13px;
          color: var(--espresso);
          font-weight: 400;
        }

        .value-sub {
          font-size: 12px;
          color: var(--mocha);
          font-weight: 300;
          margin-top: 2px;
        }

        /* ── PROCESS ── */
        .process-section { 
          background: var(--espresso);
          position: relative;
        }

        .process-section .section-h2 { color: var(--cream); }

        .process-section .section-sub { color: var(--mocha); }

        .process-carousel {
          max-width: 600px;
          margin: 0 auto;
          position: relative;
          padding: 0 60px;
        }

        .process-step-display {
          background: var(--espresso-mid);
          border: 0.5px solid var(--espresso-mid);
          padding: 48px 40px;
          text-align: center;
          min-height: 240px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          transition: all 0.3s ease;
        }

        .process-num {
          font-size: 10px;
          letter-spacing: 0.14em;
          color: var(--warm-gold);
          margin-bottom: 12px;
        }

        .process-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 28px;
          font-weight: 400;
          color: var(--cream);
          margin-bottom: 12px;
        }

        .process-desc {
          font-size: 14px;
          color: var(--mocha);
          line-height: 1.7;
          font-weight: 300;
        }

        .process-nav-btn {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background: var(--warm-gold);
          border: none;
          width: 44px;
          height: 44px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          color: var(--espresso);
          font-size: 18px;
          font-weight: 300;
          z-index: 10;
        }

        .process-nav-btn:hover {
          background: var(--gold-light);
          transform: translateY(-50%) scale(1.1);
        }

        .process-nav-btn.prev {
          left: 0;
        }

        .process-nav-btn.next {
          right: 0;
        }

        .process-indicators {
          display: flex;
          justify-content: center;
          gap: 8px;
          margin-top: 24px;
        }

        .process-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: var(--espresso-mid);
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .process-dot.active {
          background: var(--warm-gold);
          width: 24px;
          border-radius: 4px;
        }

        .process-dot:hover {
          background: var(--warm-gold);
          opacity: 0.7;
        }

        /* ── MYSTERY SHOPPING ── */
        .mystery-section {
          background: var(--sand);
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 64px;
          align-items: start;
        }

        .mystery-h {
          font-family: 'Cormorant Garamond', serif;
          font-size: 30px;
          font-weight: 300;
          color: var(--espresso);
          line-height: 1.28;
          margin-bottom: 16px;
        }

        .mystery-body {
          font-size: 14px;
          color: var(--mocha);
          line-height: 1.82;
          font-weight: 300;
          margin-bottom: 24px;
        }

        .mystery-features {
          display: flex;
          flex-direction: column;
          gap: 10px;
          margin-bottom: 28px;
        }

        .mystery-feat {
          display: flex;
          gap: 10px;
          align-items: flex-start;
          font-size: 13px;
          color: var(--mocha);
          font-weight: 300;
        }

        .feat-dot {
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: var(--warm-gold);
          flex-shrink: 0;
          margin-top: 6px;
        }

        .mystery-panel {
          background: var(--espresso);
          padding: 36px 32px;
        }

        .panel-label {
          font-size: 9px;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: var(--warm-gold);
          margin-bottom: 20px;
        }

        .journey-step {
          display: flex;
          gap: 14px;
          align-items: flex-start;
          margin-bottom: 18px;
          padding-bottom: 18px;
          border-bottom: 0.5px solid var(--espresso-mid);
        }

        .journey-step:last-child {
          margin-bottom: 0;
          padding-bottom: 0;
          border-bottom: none;
        }

        .journey-num {
          font-size: 10px;
          letter-spacing: 0.1em;
          color: var(--warm-gold);
          min-width: 22px;
          padding-top: 2px;
        }

        .journey-title {
          font-size: 13px;
          color: var(--cream);
          margin-bottom: 3px;
          font-weight: 400;
        }

        .journey-desc {
          font-size: 12px;
          color: var(--mocha);
          font-weight: 300;
          line-height: 1.5;
        }

        /* ── TESTIMONIAL ── */
        .testimonial-section {
          background: var(--cream);
          text-align: center;
          border-top: 0.5px solid var(--warm-stone);
          padding-bottom: 48px;
        }

        .testimonial-quote {
          font-family: 'Cormorant Garamond', serif;
          font-size: 24px;
          font-weight: 300;
          font-style: italic;
          color: var(--espresso);
          line-height: 1.55;
          max-width: 560px;
          margin: 0 auto 16px;
        }

        .testimonial-attr {
          font-size: 10.5px;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--mocha);
        }

        .testimonial-gold-rule {
          width: 24px;
          height: 0.5px;
          background: var(--warm-gold);
          margin: 0 auto 28px;
        }

        /* ── EXPERTISE CREDENTIALS ── */
        .expertise-section {
          background: var(--sand);
          padding: 60px 0;
          border-top: 0.5px solid var(--warm-stone);
          overflow: hidden;
        }

        .expertise-intro {
          text-align: center;
          margin-bottom: 48px;
          padding: 0 48px;
        }

        .expertise-label {
          font-size: 9px;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: var(--warm-gold);
          margin-bottom: 12px;
        }

        .expertise-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 26px;
          font-weight: 300;
          color: var(--espresso);
          max-width: 600px;
          margin: 0 auto;
          line-height: 1.4;
        }

        .expertise-carousel-wrapper {
          position: relative;
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 80px;
        }

        .expertise-carousel {
          display: flex;
          gap: 24px;
          overflow-x: auto;
          scroll-behavior: smooth;
          scrollbar-width: none;
          -ms-overflow-style: none;
          padding: 20px 0 40px;
        }

        .expertise-carousel::-webkit-scrollbar {
          display: none;
        }

        .expertise-card {
          min-width: 380px;
          background: #5A4534;
          border-radius: 24px;
          padding: 48px 36px;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          position: relative;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          cursor: pointer;
        }

        .expertise-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 16px 32px rgba(44, 31, 20, 0.25);
          background: #6B5744;
        }

        .expertise-card-number {
          font-family: 'DM Sans', sans-serif;
          font-size: 72px;
          font-weight: 700;
          color: rgba(245, 240, 232, 0.15);
          line-height: 1;
          margin-bottom: 24px;
        }

        .expertise-card-icon {
          font-size: 80px;
          color: var(--cream);
          margin-bottom: 32px;
          font-weight: 300;
          font-family: 'Cormorant Garamond', serif;
          line-height: 1;
        }

        .expertise-card-title {
          font-size: 11px;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--cream);
          margin-bottom: 16px;
          font-weight: 500;
        }

        .expertise-card-items {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .expertise-card-items li {
          font-size: 13px;
          color: rgba(245, 240, 232, 0.7);
          margin-bottom: 8px;
          font-weight: 300;
          line-height: 1.5;
        }

        .expertise-card-items li:last-child {
          margin-bottom: 0;
        }

        .expertise-nav {
          position: absolute;
          bottom: -60px;
          right: 80px;
          display: flex;
          gap: 16px;
        }

        .expertise-nav-btn {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          border: 1.5px solid #5A4534;
          background: transparent;
          color: #5A4534;
          font-size: 24px;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .expertise-nav-btn:hover {
          background: #5A4534;
          color: var(--cream);
          transform: scale(1.05);
        }

        @media (max-width: 768px) {
          .expertise-carousel-wrapper {
            padding: 0 24px;
          }

          .expertise-card {
            min-width: 300px;
          }

          .expertise-nav {
            right: 24px;
          }

          .expertise-nav-btn {
            width: 48px;
            height: 48px;
            font-size: 20px;
          }
        }

        @media (max-width: 768px) {
          .expertise-grid {
            grid-template-columns: 1fr 1fr 1fr;
          }
        }

        @media (max-width: 480px) {
          .expertise-grid {
            grid-template-columns: 1fr;
          }
          .expertise-category {
            border-right: none;
            border-bottom: 0.5px solid var(--warm-stone);
          }
          .expertise-category:last-child {
            border-bottom: none;
          }
        }

        /* ── CALENDLY MODAL ── */
        .calendly-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(44, 31, 20, 0.9);
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          backdrop-filter: blur(4px);
        }

        .calendly-modal-content {
          background: var(--cream);
          max-width: 1000px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          position: relative;
          border: 0.5px solid var(--warm-stone);
        }

        .calendly-modal-header {
          padding: 24px 32px;
          border-bottom: 0.5px solid var(--warm-stone);
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: var(--sand);
        }

        .calendly-modal-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 24px;
          font-weight: 300;
          color: var(--espresso);
        }

        .calendly-close-btn {
          background: none;
          border: none;
          font-size: 28px;
          color: var(--mocha);
          cursor: pointer;
          padding: 0;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
          line-height: 1;
        }

        .calendly-close-btn:hover {
          color: var(--espresso);
          transform: rotate(90deg);
        }

        .calendly-modal-body {
          padding: 0;
        }

        @media (max-width: 768px) {
          .calendly-modal-content {
            max-width: 100%;
            max-height: 100vh;
          }
        }

        /* ── BLOG & NEWSLETTER ── */
        .blog-section {
          background: var(--cream);
          padding: 80px 48px;
        }

        .blog-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 32px;
          max-width: 1200px;
          margin: 0 auto 60px;
        }

        .blog-card {
          background: var(--sand);
          border: 0.5px solid var(--warm-stone);
          transition: all 0.3s ease;
          cursor: pointer;
          overflow: hidden;
        }

        .blog-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 16px rgba(44, 31, 20, 0.1);
        }

        .blog-image {
          width: 100%;
          height: 200px;
          object-fit: cover;
          transition: transform 0.3s ease;
        }

        .blog-card:hover .blog-image {
          transform: scale(1.05);
        }

        .blog-content {
          padding: 24px;
        }

        .blog-meta {
          font-size: 9px;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--warm-gold);
          margin-bottom: 10px;
        }

        .blog-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 20px;
          font-weight: 400;
          color: var(--espresso);
          margin-bottom: 10px;
          line-height: 1.3;
        }

        .blog-excerpt {
          font-size: 13px;
          color: var(--mocha);
          line-height: 1.7;
          font-weight: 300;
        }

        .newsletter-box {
          background: var(--espresso);
          padding: 48px;
          max-width: 800px;
          margin: 0 auto;
          text-align: center;
        }

        .newsletter-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 28px;
          font-weight: 300;
          color: var(--cream);
          margin-bottom: 12px;
        }

        .newsletter-desc {
          font-size: 14px;
          color: var(--mocha);
          margin-bottom: 28px;
          font-weight: 300;
        }

        .newsletter-form {
          display: flex;
          gap: 12px;
          max-width: 500px;
          margin: 0 auto;
        }

        .newsletter-input {
          flex: 1;
          padding: 12px 20px;
          border: 0.5px solid var(--espresso-mid);
          background: var(--espresso-mid);
          color: var(--cream);
          font-size: 12px;
          font-family: 'DM Sans', sans-serif;
          transition: all 0.3s ease;
        }

        .newsletter-input::placeholder {
          color: var(--mocha);
        }

        .newsletter-input:focus {
          outline: none;
          border-color: var(--warm-gold);
          background: var(--espresso-deep);
        }

        .newsletter-btn {
          padding: 12px 28px;
          background: var(--warm-gold);
          color: var(--espresso);
          border: none;
          font-size: 10px;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
          white-space: nowrap;
        }

        .newsletter-btn:hover {
          background: var(--gold-light);
        }

        @media (max-width: 768px) {
          .blog-grid {
            grid-template-columns: 1fr;
          }
          .newsletter-form {
            flex-direction: column;
          }
        }
        .faq-section { background: var(--sand); }

        .faq-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0 48px;
          max-width: 860px;
          margin: 0 auto;
        }

        .faq-item {
          padding: 20px 0;
          border-bottom: 0.5px solid var(--warm-stone);
        }

        .faq-q {
          font-size: 14px;
          font-weight: 500;
          color: var(--espresso);
          margin-bottom: 8px;
          line-height: 1.4;
        }

        .faq-a {
          font-size: 13px;
          color: var(--mocha);
          line-height: 1.75;
          font-weight: 300;
        }

        /* ── CTA ── */
        .cta-section {
          background: var(--espresso);
          text-align: center;
          padding: 96px 48px;
        }

        .cta-section .gold-rule { margin-bottom: 24px; }

        .cta-h {
          font-family: 'Cormorant Garamond', serif;
          font-size: 38px;
          font-weight: 300;
          color: var(--cream);
          line-height: 1.22;
          margin-bottom: 16px;
        }

        .cta-sub {
          font-size: 14px;
          color: var(--mocha);
          line-height: 1.8;
          margin-bottom: 32px;
          font-weight: 300;
        }

        .btn-gold {
          font-size: 10px;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          background: var(--warm-gold);
          color: var(--espresso);
          padding: 14px 36px;
          text-decoration: none;
          font-weight: 500;
          transition: background 0.2s;
          display: inline-block;
        }

        .btn-gold:hover { background: var(--gold-light); }

        .cta-note {
          font-size: 11.5px;
          color: var(--espresso-mid);
          margin-top: 16px;
          letter-spacing: 0.04em;
        }

        /* ── FOOTER ── */
        footer {
          background: var(--espresso-deep);
          padding: 32px 48px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .footer-brand {
          font-size: 9px;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: var(--warm-gold);
          font-weight: 500;
        }

        .footer-copy {
          font-size: 12px;
          color: var(--mocha);
          font-weight: 400;
        }

        .footer-links {
          display: flex;
          gap: 20px;
        }

        .footer-links a {
          font-size: 11px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--mocha);
          text-decoration: none;
          transition: color 0.2s;
        }

        .footer-links a:hover { color: var(--warm-gold); }

        .footer-social {
          display: flex;
          gap: 16px;
          align-items: center;
        }

        .footer-social a {
          color: var(--warm-gold);
          text-decoration: none;
          font-size: 20px;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .footer-social a:hover {
          color: var(--gold-light);
          transform: translateY(-2px);
        }

        /* ── RESPONSIVE ── */
        @media (max-width: 1024px) {
          .services-grid { grid-template-columns: 1fr 1fr; }
          .expertise-carousel-wrapper { padding: 0 48px; }
          .expertise-nav { right: 48px; }
        }

        @media (max-width: 768px) {
          /* Navigation */
          nav { 
            padding: 14px 20px;
            flex-wrap: wrap;
          }
          .nav-links { display: none; }
          .nav-cta {
            font-size: 9px;
            padding: 10px 20px;
          }

          /* Hero */
          .hero { 
            padding: 80px 24px 60px;
            min-height: 80vh;
          }
          .hero h1 { 
            font-size: 36px;
            margin-bottom: 18px;
          }
          .hero-sub {
            font-size: 14px;
            max-width: 100%;
          }
          .hero-actions {
            flex-direction: column;
            gap: 12px;
            width: 100%;
          }
          .btn-primary, .btn-ghost {
            width: 100%;
            text-align: center;
            justify-content: center;
          }

          /* Stats Bar */
          .stats-bar {
            grid-template-columns: 1fr;
            gap: 20px;
            padding: 32px 24px;
          }
          .stat-item {
            text-align: center;
            border-right: none;
            border-bottom: 0.5px solid var(--warm-stone);
            padding-bottom: 20px;
          }
          .stat-item:last-child {
            border-bottom: none;
            padding-bottom: 0;
          }

          /* Sections */
          section { padding: 56px 24px; }
          
          .section-h2 {
            font-size: 32px;
          }
          
          /* Services */
          .services-section {
            padding: 60px 0 100px;
          }
          .services-swiper-container {
            padding: 40px 20px;
          }
          .svc-card {
            width: 280px;
            min-height: 360px;
            padding: 24px 20px;
          }
          .swiper-button-next,
          .swiper-button-prev {
            width: 40px;
            height: 40px;
          }
          .swiper-button-next:after,
          .swiper-button-prev:after {
            font-size: 16px;
          }

          /* About */
          .about-section { 
            grid-template-columns: 1fr;
            gap: 40px;
            padding: 56px 24px;
          }
          .about-visual {
            min-height: 320px;
            order: -1;
          }

          /* Process */
          .process-carousel-card {
            padding: 40px 28px;
          }
          .process-nav {
            gap: 12px;
          }
          .process-nav-btn {
            width: 48px;
            height: 48px;
          }

          /* Mystery Shopping */
          .mystery-section { 
            grid-template-columns: 1fr;
            gap: 40px;
          }
          .mystery-visual {
            min-height: 320px;
          }

          /* Expertise Carousel */
          .expertise-intro {
            padding: 0 24px;
            margin-bottom: 36px;
          }
          .expertise-title {
            font-size: 22px;
          }
          .expertise-carousel-wrapper {
            padding: 0 24px;
          }
          .expertise-card {
            min-width: 280px;
            padding: 36px 28px;
          }
          .expertise-card-number {
            font-size: 56px;
          }
          .expertise-card-icon {
            font-size: 64px;
          }
          .expertise-nav {
            right: 24px;
            bottom: -56px;
          }
          .expertise-nav-btn {
            width: 44px;
            height: 44px;
            font-size: 18px;
          }

          /* FAQ */
          .faq-grid { 
            grid-template-columns: 1fr;
            gap: 20px;
          }
          .faq-item {
            padding: 24px;
          }

          /* Blog */
          .blog-grid {
            grid-template-columns: 1fr;
          }

          /* CTA */
          .cta-h {
            font-size: 32px;
          }
          .cta-sub {
            font-size: 14px;
          }

          /* Footer */
          footer { 
            flex-direction: column;
            gap: 16px;
            text-align: center;
            padding: 32px 24px;
          }
          .footer-links {
            flex-direction: column;
            gap: 12px;
          }
        }

        @media (max-width: 480px) {
          /* Hero */
          .hero {
            padding: 60px 20px 48px;
          }
          .hero h1 { 
            font-size: 28px;
            line-height: 1.25;
          }
          .eyebrow {
            font-size: 9px;
          }
          .hero-sub {
            font-size: 13px;
          }

          /* Services */
          .svc-icon {
            font-size: 32px;
          }
          .svc-title {
            font-size: 16px;
          }
          .svc-desc {
            font-size: 12px;
          }

          /* Process */
          .process-carousel-card {
            padding: 32px 24px;
          }
          .process-step-num {
            font-size: 56px;
          }
          .process-step-title {
            font-size: 20px;
          }

          /* Expertise */
          .expertise-card {
            min-width: 260px;
            padding: 32px 24px;
          }
          .expertise-card-number {
            font-size: 48px;
          }
          .expertise-card-icon {
            font-size: 56px;
          }

          /* Section Titles */
          .section-h2 {
            font-size: 28px;
          }
          .about-h, .testimonial-quote {
            font-size: 24px;
          }

          /* CTA */
          .cta-h {
            font-size: 26px;
          }
        }

        @media (max-width: 360px) {
          .hero h1 {
            font-size: 24px;
          }
          .section-h2 {
            font-size: 24px;
          }
          .expertise-card {
            min-width: 240px;
          }
        }
      `}</style>

      <div>
        {/* Calendly Modal */}
        {showCalendly && (
          <div className="calendly-modal-overlay" onClick={closeCalendly}>
            <div className="calendly-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="calendly-modal-header">
                <h2 className="calendly-modal-title">Book Your Free Discovery Call</h2>
                <button className="calendly-close-btn" onClick={closeCalendly}>×</button>
              </div>
              <div className="calendly-modal-body">
                <div 
                  className="calendly-inline-widget" 
                  data-url="https://calendly.com/stephanieluxesolutions/30min?hide_gdpr_banner=1&primary_color=ff6500" 
                  style={{minWidth: '320px', height: '700px'}}
                ></div>
              </div>
            </div>
          </div>
        )}

        {/* NAVIGATION */}
        <nav>
          <a href="#" className="nav-brand">
            <div className="nav-monogram">
              <span className="fade">S</span><span>L</span><span className="fade">S</span>
            </div>
            <div className="nav-wordmark">Stephanies Luxe Solutions</div>
          </a>
          <div className="nav-links">
            <a href="#services">Services</a>
            <a href="#about">About</a>
            <a href="#process">Process</a>
            <a href="#mystery">Mystery Shopping</a>
            <a href="#blog">Blog</a>
            <a href="#faq">FAQ</a>
          </div>
          <a href="#contact" className="nav-cta" onClick={(e) => { e.preventDefault(); openCalendly(); }}>Book a call</a>
        </nav>

        {/* HERO */}
        <div className="hero">
          <video 
            autoPlay 
            loop 
            muted 
            playsInline
            className="hero-video-bg"
          >
            <source src="https://customer-assets.emergentagent.com/job_airbnb-strategy-lab/artifacts/w9ou3g9r_210905.mp4" type="video/mp4" />
          </video>
          <div className="hero-overlay"></div>
          <div className="hero-content">
            <div className="eyebrow" style={{fontWeight: 600, color: '#6B5744'}}>Short-term rental consultancy · UAE</div>
            <div className="gold-rule"></div>
            <h1>Your short-term rental deserves to be the one guests never forget.</h1>
            <p className="hero-sub">UAE property owner, STR operator and international investor — we turn your property into a high-performing asset guests can't stop talking about.</p>
            <div className="hero-actions">
              <a href="#contact" className="btn-primary">Book your free discovery call</a>
              <a href="#services" className="btn-ghost">View services</a>
            </div>
          </div>
        </div>

        {/* STATS BAR */}
        <div className="stats-bar">
          <div className="stat-item">
            <div className="stat-num">UAE-rooted</div>
            <div className="stat-label">Market focus</div>
          </div>
          <div className="stat-item">
            <div className="stat-num">Premium services</div>
            <div className="stat-label">End-to-end coverage</div>
          </div>
          <div className="stat-item">
            <div className="stat-num">Detail-first</div>
            <div className="stat-label">Our approach</div>
          </div>
        </div>

        {/* SERVICES */}
        <section className="services-section" id="services">
          <div className="section-eyebrow">What we do</div>
          <h2 className="section-h2">Everything your STR operation needs.<br/>Nothing it doesn't.</h2>
          <p className="section-sub">A focused set of services — each targeting a specific gap in how short-term rentals perform across the UAE.</p>
          
          <div className="services-swiper-container">
            <Swiper
              effect={'coverflow'}
              grabCursor={true}
              centeredSlides={true}
              slidesPerView={3}
              initialSlide={0}
              loop={false}
              coverflowEffect={{
                rotate: 15,
                stretch: 10,
                depth: 150,
                modifier: 1.5,
                slideShadows: false,
              }}
              navigation={true}
              pagination={{ clickable: true }}
              modules={[EffectCoverflow, Navigation, Pagination]}
              className="services-swiper"
              breakpoints={{
                320: {
                  slidesPerView: 1,
                  spaceBetween: 20
                },
                768: {
                  slidesPerView: 2,
                  spaceBetween: 30
                },
                1024: {
                  slidesPerView: 3,
                  spaceBetween: 40
                }
              }}
            >
              {/* Service 01 - Mystery Shopping (Featured) */}
              <SwiperSlide>
                <div className="svc-card featured">
                  <div className="svc-icon">⊜</div>
                  <div className="svc-num">01</div>
                  <div className="svc-title light">Mystery shopping</div>
                  <div className="svc-desc light">We stay as a guest. We document everything. You see exactly what your guests experience — and precisely how to elevate it.</div>
                  <div className="svc-badge">Signature service</div>
                </div>
              </SwiperSlide>

              {/* Service 02 */}
              <SwiperSlide>
                <Link to="/listing-pricing" style={{ textDecoration: 'none', display: 'block' }}>
                  <div className="svc-card">
                    <div className="svc-icon">✎</div>
                    <div className="svc-num">02</div>
                    <div className="svc-title">Listing & pricing management</div>
                    <div className="svc-desc">Professional listing, platform optimisation, and dynamic pricing strategy — so your property earns what it's worth every night of the year.</div>
                    <div className="svc-badge">View pricing</div>
                  </div>
                </Link>
              </SwiperSlide>

              {/* Service 03 */}
              <SwiperSlide>
                <div className="svc-card">
                  <div className="svc-icon">◉</div>
                  <div className="svc-num">03</div>
                  <div className="svc-title">STR performance audit</div>
                  <div className="svc-desc">A rigorous, data-led review of occupancy, pricing, reviews, listing quality, and competitor positioning — with a prioritised action plan.</div>
                </div>
              </SwiperSlide>

              {/* Service 04 */}
              <SwiperSlide>
                <div className="svc-card">
                  <div className="svc-icon">⚑</div>
                  <div className="svc-num">04</div>
                  <div className="svc-title">Airbnb | Holiday Home Business Setup & Strategy</div>
                  <div className="svc-desc">Whether you're launching your first Airbnb in Dubai or scaling a holiday home portfolio across the UAE, we build your go-to-market plan, revenue model, platform strategy, and DTCM licensing roadmap from the ground up — so you start right and grow faster.</div>
                </div>
              </SwiperSlide>

              {/* Service 05 */}
              <SwiperSlide>
                <div className="svc-card">
                  <div className="svc-icon">★</div>
                  <div className="svc-num">05</div>
                  <div className="svc-title">Guest experience design</div>
                  <div className="svc-desc">Every touchpoint of your guest journey — designed for 5-star reviews, repeat bookings, and guests who tell their friends.</div>
                </div>
              </SwiperSlide>

              {/* Service 06 */}
              <SwiperSlide>
                <div className="svc-card">
                  <div className="svc-icon">⊙</div>
                  <div className="svc-num">06</div>
                  <div className="svc-title">Apartment | Villa Operational Coordination & Optimisation</div>
                  <div className="svc-desc">From housekeeping standards and maintenance coordination to SOPs and team accountability systems — we build the operational framework that keeps your Dubai apartment or UAE villa running consistently, professionally, and at scale.</div>
                </div>
              </SwiperSlide>

              {/* Service 07 */}
              <SwiperSlide>
                <div className="svc-card">
                  <div className="svc-icon">☙</div>
                  <div className="svc-num">07</div>
                  <div className="svc-title">Vendor & Partner Referrals</div>
                  <div className="svc-desc">Trusted vendor connections and curated STR property opportunities across the UAE — vetted partners and properties that meet our quality standards.</div>
                </div>
              </SwiperSlide>
            </Swiper>
          </div>
        </section>

        {/* TRUSTED PARTNERS CAROUSEL */}
        <section 
          aria-label="Trusted Partners — STR Operations Tools UAE"
          style={{ 
            background: 'var(--cream)', 
            padding: '100px 48px',
          }}
        >
          <style>{`
            .trusted-partner-container {
              max-width: 900px;
              margin: 0 auto;
            }

            .trusted-partner-label {
              font-size: 12px;
              letter-spacing: 0.16em;
              text-transform: uppercase;
              color: var(--warm-gold);
              font-weight: 600;
              margin-bottom: 24px;
              text-align: center;
            }

            .partners-carousel-wrapper {
              position: relative;
              overflow: hidden;
              width: 100%;
            }

            .partners-carousel-track {
              display: flex;
              animation: partnerSlide 8s infinite;
              will-change: transform;
            }

            @keyframes partnerSlide {
              0% { transform: translateX(0%); }
              45% { transform: translateX(0%); }
              50% { transform: translateX(-100%); }
              95% { transform: translateX(-100%); }
              100% { transform: translateX(0%); }
            }

            .partner-card {
              flex: 0 0 100%;
              width: 100%;
              background: var(--espresso);
              border-radius: 16px;
              padding: 48px;
              min-height: 480px;
            }

            .partner-header {
              display: flex;
              align-items: center;
              justify-content: space-between;
              margin-bottom: 24px;
            }

            .partner-logo {
              height: 32px;
              filter: brightness(0) invert(1);
            }

            .partner-logo-text {
              font-family: 'Cormorant Garamond', serif;
              font-size: 28px;
              font-weight: 400;
              color: #F5F0E8;
              letter-spacing: 0.15em;
              text-transform: uppercase;
            }

            .partner-badge {
              padding: 6px 16px;
              border: 1px solid var(--warm-gold);
              color: var(--warm-gold);
              font-size: 10px;
              letter-spacing: 0.12em;
              text-transform: uppercase;
              border-radius: 20px;
              font-weight: 500;
            }

            .partner-divider {
              height: 1px;
              background: rgba(196, 168, 130, 0.25);
              margin-bottom: 32px;
            }

            .partner-heading {
              font-family: 'Cormorant Garamond', serif;
              font-size: 32px;
              font-weight: 300;
              color: var(--cream);
              margin-bottom: 20px;
              line-height: 1.3;
            }

            .partner-body {
              font-size: 15px;
              line-height: 1.8;
              color: rgba(245, 240, 232, 0.7);
              margin-bottom: 28px;
            }

            .partner-offer {
              background: rgba(196, 168, 130, 0.1);
              border: 1px solid rgba(196, 168, 130, 0.3);
              border-radius: 12px;
              padding: 24px;
              margin-bottom: 32px;
              display: flex;
              gap: 16px;
              align-items: flex-start;
            }

            .offer-icon {
              flex-shrink: 0;
              margin-top: 2px;
            }

            .offer-text {
              font-size: 14px;
              line-height: 1.7;
              color: rgba(245, 240, 232, 0.85);
            }

            .offer-highlight {
              color: var(--warm-gold);
              font-weight: 500;
            }

            .partner-cta-row {
              display: flex;
              align-items: center;
              gap: 24px;
              flex-wrap: wrap;
            }

            .partner-btn {
              padding: 14px 32px;
              background: var(--warm-gold);
              color: var(--espresso);
              font-size: 12px;
              letter-spacing: 0.1em;
              text-transform: uppercase;
              font-weight: 600;
              border: none;
              border-radius: 4px;
              text-decoration: none;
              display: inline-block;
              cursor: pointer;
              transition: all 0.3s ease;
            }

            .partner-btn:hover {
              background: var(--gold-light);
              transform: translateY(-1px);
            }

            .cta-subtext {
              font-size: 12px;
              color: rgba(245, 240, 232, 0.5);
            }

            .sr-only {
              position: absolute;
              width: 1px;
              height: 1px;
              padding: 0;
              margin: -1px;
              overflow: hidden;
              clip: rect(0, 0, 0, 0);
              white-space: nowrap;
              border-width: 0;
            }

            @media (max-width: 768px) {
              .partner-card {
                padding: 32px 24px;
                min-height: 520px;
              }

              .partner-header {
                flex-direction: column;
                align-items: flex-start;
                gap: 16px;
              }

              .partner-heading {
                font-size: 26px;
              }

              .partner-body {
                font-size: 14px;
              }

              .partner-cta-row {
                flex-direction: column;
                align-items: flex-start;
              }

              .partner-logo-text {
                font-size: 24px;
              }
            }
          `}</style>

          <div className="trusted-partner-container">
            <div className="trusted-partner-label">Trusted Partners</div>
            
            <div className="partners-carousel-wrapper">
              <div className="partners-carousel-track">
                
                {/* BREEZEWAY CARD */}
                <div className="partner-card">
                  <div className="partner-header">
                    <img 
                      src="https://www.breezeway.io/hubfs/breezeway_logo.svg.svg" 
                      alt="Breezeway property operations platform — recommended by Stephanie's Luxe Solutions, UAE STR consultants" 
                      className="partner-logo"
                    />
                    <div className="partner-badge">Recommended Partner</div>
                  </div>

                  <div className="partner-divider"></div>

                  <h3 className="partner-heading">
                    Operational excellence for Airbnb hosts and holiday home managers — powered by the right systems.
                  </h3>

                  <p className="partner-body">
                    At Stephanie's Luxe Solutions, we only recommend what we stand behind. Breezeway is the go-to property operations platform for Airbnb hosts, holiday home operators, and short-term rental managers across Dubai, Abu Dhabi, and Ras Al Khaimah when it's time to build systems that hold — from automated turnovers and inspection workflows to maintenance tracking and team communication.
                  </p>

                  <p className="sr-only" aria-hidden="false">
                    Breezeway is a property care and operations tool recommended for UAE STR operators, Airbnb hosts, and holiday home managers by Stephanie's Luxe Solutions.
                  </p>

                  <div className="partner-offer">
                    <div className="offer-icon">
                      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="3" y="13" width="22" height="13" rx="2" fill="#C4A882" opacity="0.3"/>
                        <rect x="3" y="13" width="22" height="13" rx="2" stroke="#C4A882" strokeWidth="1.2"/>
                        <rect x="1" y="9" width="26" height="5" rx="1.5" fill="#C4A882" opacity="0.2"/>
                        <rect x="1" y="9" width="26" height="5" rx="1.5" stroke="#C4A882" strokeWidth="1.2"/>
                        <line x1="14" y1="9" x2="14" y2="26" stroke="#C4A882" strokeWidth="1.2"/>
                        <path d="M14 9C14 9 10 7 10 4.5C10 3 11 2 12.5 2C13.5 2 14 3 14 3" stroke="#C4A882" strokeWidth="1.2" strokeLinecap="round"/>
                        <path d="M14 9C14 9 18 7 18 4.5C18 3 17 2 15.5 2C14.5 2 14 3 14 3" stroke="#C4A882" strokeWidth="1.2" strokeLinecap="round"/>
                        <path d="M14 9C14 9 10 7 10 4.5C10 3 11 2 12.5 2C13.5 2 14 3 14 3" stroke="#C4A882" strokeWidth="1.2" strokeLinecap="round"/>
                      </svg>
                    </div>
                    <div className="offer-text">
                      Sign up through our exclusive partner program and receive <span className="offer-highlight">complimentary implementation support</span> — our exclusive offer for clients and connections who are ready to build properly from day one. Mention this offer when submitting our inquiry form below.
                    </div>
                  </div>

                  <div className="partner-cta-row">
                    <a 
                      href="#contact" 
                      className="partner-btn"
                    >
                      Get started with Breezeway
                    </a>
                    <span className="cta-subtext">
                      Request your access link via our contact form to activate your offer.
                    </span>
                  </div>
                </div>

                {/* KORA CARD */}
                <div className="partner-card">
                  <div className="partner-header">
                    <div className="partner-logo-text">KORA</div>
                    <div className="partner-badge">Official Partner</div>
                  </div>

                  <div className="partner-divider"></div>

                  <h3 className="partner-heading">
                    Company formation and business licensing, handled with precision.
                  </h3>

                  <p className="partner-body">
                    KORA are our official partners for company formation and business licensing in the UAE. When our clients are ready to launch or formalise their holiday home operation, KORA are the specialists we trust to get it done — properly, efficiently, and to the standard SLS requires.
                  </p>
                </div>

              </div>
            </div>
          </div>
        </section>

        {/* ABOUT */}
        <section className="about-section" id="about">
          <div className="about-visual">
            <img 
              src="https://customer-assets.emergentagent.com/job_airbnb-strategy-lab/artifacts/8kv3gc94_pexels-juan-tapias-695500451-29559675.jpg" 
              alt="Luxury short-term rental interior showcasing high-end design and premium finishes in UAE holiday home"
            />
          </div>
          <div className="about-content">
            <div className="eyebrow" style={{textAlign: 'left'}}>About us</div>
            <h2 className="about-h">Built on the belief that every detail matters.</h2>
            <p className="about-body">The UAE's short-term rental market has grown fast. Too fast, in some ways — with a wave of new listings that look the part but don't deliver the experience guests expect, or that owners deserve to be proud of.</p>
            <p className="about-body">We started SLS because we believe the UAE can set the global standard for STR hospitality. And that starts with operators who care enough to get it right. That's who we work with.</p>
            <div className="about-values">
              <div className="about-value">
                <div className="value-dot"></div>
                <div>
                  <div className="value-text">Detail is the difference</div>
                  <div className="value-sub">The things operators overlook are exactly what guests notice.</div>
                </div>
              </div>
              <div className="about-value">
                <div className="value-dot"></div>
                <div>
                  <div className="value-text">Honest before comfortable</div>
                  <div className="value-sub">Our audits tell the truth — with a clear path to fix it.</div>
                </div>
              </div>
              <div className="about-value">
                <div className="value-dot"></div>
                <div>
                  <div className="value-text">UAE-deep, globally informed</div>
                  <div className="value-sub">Local market knowledge paired with world-class best practice.</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* PROCESS */}
        <section className="process-section" id="process">
          <div className="section-eyebrow">How we work</div>
          <h2 className="section-h2">Rigorous by design.</h2>
          <p className="section-sub" style={{color: 'var(--mocha)'}}>Every engagement follows a clear, structured process — so you always know what's happening and why.</p>
          
          <div className="process-carousel">
            <button className="process-nav-btn prev" onClick={prevStep}>←</button>
            <button className="process-nav-btn next" onClick={nextStep}>→</button>
            
            <div className="process-step-display">
              <div className="process-num">{processSteps[currentStep].num}</div>
              <div className="process-title">{processSteps[currentStep].title}</div>
              <div className="process-desc">{processSteps[currentStep].desc}</div>
            </div>

            <div className="process-indicators">
              {processSteps.map((_, index) => (
                <div 
                  key={index}
                  className={`process-dot ${currentStep === index ? 'active' : ''}`}
                  onClick={() => setCurrentStep(index)}
                />
              ))}
            </div>
          </div>
        </section>

        {/* MYSTERY SHOPPING */}
        <section className="mystery-section" id="mystery">
          <div>
            <div className="eyebrow" style={{textAlign: 'left'}}>Signature service</div>
            <h2 className="mystery-h">We experience your property exactly as a guest would.</h2>
            <p className="mystery-body">Most consultants review data. We book a real stay — unannounced — and go through your entire guest journey from the first message to checkout. Then we show you everything, in detail.</p>
            <div className="mystery-features">
              <div className="mystery-feat"><div className="feat-dot"></div><div>Real booking, real stay — no advance notice given</div></div>
              <div className="mystery-feat"><div className="feat-dot"></div><div>Every touchpoint documented and scored</div></div>
              <div className="mystery-feat"><div className="feat-dot"></div><div>Detailed report with exact improvement steps</div></div>
              <div className="mystery-feat"><div className="feat-dot"></div><div>Available as standalone or within a full audit</div></div>
            </div>
            <a href="#contact" className="btn-primary" onClick={(e) => { e.preventDefault(); openCalendly(); }}>Enquire about mystery shopping</a>
          </div>
          <div className="mystery-panel">
            <div className="panel-label">Guest journey touchpoints</div>
            <div className="journey-step">
              <div className="journey-num">01</div>
              <div>
                <div className="journey-title">Search & discovery</div>
                <div className="journey-desc">Listing quality, photography, pricing vs. comps</div>
              </div>
            </div>
            <div className="journey-step">
              <div className="journey-num">02</div>
              <div>
                <div className="journey-title">Booking & pre-arrival</div>
                <div className="journey-desc">Communication speed, clarity, and tone</div>
              </div>
            </div>
            <div className="journey-step">
              <div className="journey-num">03</div>
              <div>
                <div className="journey-title">Check-in experience</div>
                <div className="journey-desc">Access, welcome setup, and first impression</div>
              </div>
            </div>
            <div className="journey-step">
              <div className="journey-num">04</div>
              <div>
                <div className="journey-title">In-stay quality</div>
                <div className="journey-desc">Cleanliness, amenities, and detail execution</div>
              </div>
            </div>
            <div className="journey-step">
              <div className="journey-num">05</div>
              <div>
                <div className="journey-title">Checkout & follow-up</div>
                <div className="journey-desc">Process, review prompts, and guest retention</div>
              </div>
            </div>
          </div>
        </section>

        {/* TESTIMONIAL */}
        <section className="testimonial-section">
          <div className="section-eyebrow">Client results</div>
          <div className="testimonial-gold-rule"></div>
          <p className="testimonial-quote">"The level of detail in the mystery shop report was unlike anything I'd seen — it changed how we run every property."</p>
          <div className="testimonial-attr">STR Portfolio Operator · Dubai</div>
        </section>

        {/* EXPERTISE CREDENTIALS */}
        <section className="expertise-section">
          <div className="expertise-intro">
            <div className="expertise-label">Team expertise</div>
            <h3 className="expertise-title">Certified experts across every platform and tool that matters in UAE hospitality</h3>
          </div>
          
          <div className="expertise-carousel-wrapper">
            <div className="expertise-carousel">
              <div className="expertise-card">
                <div className="expertise-card-number">01</div>
                <div className="expertise-card-icon">⌗</div>
                <div className="expertise-card-title">Hosting</div>
                <ul className="expertise-card-items">
                  <li>Airbnb Superhost</li>
                  <li>TruBroker</li>
                  <li>Bayut Quality Lister</li>
                  <li>Property Finder Super Agent</li>
                </ul>
              </div>

              <div className="expertise-card">
                <div className="expertise-card-number">02</div>
                <div className="expertise-card-icon">⊕</div>
                <div className="expertise-card-title">PMS</div>
                <ul className="expertise-card-items">
                  <li>Hostaway</li>
                  <li>Uplisting</li>
                  <li>Avantio</li>
                  <li>Guesty</li>
                  <li>Lodgify</li>
                </ul>
              </div>

              <div className="expertise-card">
                <div className="expertise-card-number">03</div>
                <div className="expertise-card-icon">⊞</div>
                <div className="expertise-card-title">OTAs</div>
                <ul className="expertise-card-items">
                  <li>Airbnb</li>
                  <li>Booking.com</li>
                  <li>VRBO</li>
                  <li>Agoda</li>
                  <li>Expedia</li>
                </ul>
              </div>

              <div className="expertise-card">
                <div className="expertise-card-number">04</div>
                <div className="expertise-card-icon">◉</div>
                <div className="expertise-card-title">Pricing</div>
                <ul className="expertise-card-items">
                  <li>AirDNA</li>
                  <li>PriceLabs</li>
                  <li>Beyond</li>
                </ul>
              </div>

              <div className="expertise-card">
                <div className="expertise-card-number">05</div>
                <div className="expertise-card-icon">⊙</div>
                <div className="expertise-card-title">Ops</div>
                <ul className="expertise-card-items">
                  <li>Breezeway</li>
                  <li>Turno</li>
                  <li>Duve</li>
                </ul>
              </div>

              <div className="expertise-card">
                <div className="expertise-card-number">06</div>
                <div className="expertise-card-icon">⚑</div>
                <div className="expertise-card-title">UAE Focus</div>
                <ul className="expertise-card-items">
                  <li>Dubai Economy & Tourism</li>
                  <li>Local regulations</li>
                  <li>Market insights</li>
                  <li>Best practices</li>
                </ul>
              </div>
            </div>

            <div className="expertise-nav">
              <button className="expertise-nav-btn" onClick={() => scrollExpertiseCarousel('prev')}>
                ←
              </button>
              <button className="expertise-nav-btn" onClick={() => scrollExpertiseCarousel('next')}>
                →
              </button>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="faq-section" id="faq">
          <div className="section-eyebrow">Questions</div>
          <h2 className="section-h2">Frequently asked</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <div className="faq-q">Do you work with properties outside Dubai?</div>
              <div className="faq-a">Yes — we work with operators across the UAE including Abu Dhabi, Ras Al Khaimah, Sharjah, and Fujairah.</div>
            </div>
            <div className="faq-item">
              <div className="faq-q">I only have one or two properties. Is this right for me?</div>
              <div className="faq-a">Absolutely. Some of our best work is with single-property owners who want to do it properly from the start.</div>
            </div>
            <div className="faq-item">
              <div className="faq-q">How does the mystery shopping service work?</div>
              <div className="faq-a">We book a genuine stay at your property as a regular guest — you won't know the exact dates in advance. You receive a detailed report covering every touchpoint.</div>
            </div>
            <div className="faq-item">
              <div className="faq-q">What results can I expect?</div>
              <div className="faq-a">Clients typically see improvements in review scores, occupancy, and RevPAR within 2 weeks up to 90 days of implementing our recommendations.</div>
            </div>
            <div className="faq-item">
              <div className="faq-q">Do you offer ongoing support or retainers?</div>
              <div className="faq-a">Yes — listing and pricing management is offered as an ongoing service, and advisory retainers are available for portfolio operators.</div>
            </div>
            <div className="faq-item">
              <div className="faq-q">How do I get started?</div>
              <div className="faq-a">Book a free 30-minute discovery call. We'll talk through your situation and be honest about how we can help. No obligation, no hard sell.</div>
            </div>
          </div>
        </section>

        {/* BLOG & NEWSLETTER */}
        <section className="blog-section" id="blog">
          <div className="section-eyebrow">Insights & Updates</div>
          <h2 className="section-h2">Latest from our <span style={{fontStyle: 'italic', fontWeight: 300}}>blog</span></h2>
          <p className="section-sub">Practical insights, market updates, and proven strategies for UAE STR operators</p>
          
          <div className="blog-grid">
            {blogPosts.map((post, index) => (
              <Link 
                key={index} 
                to={`/blog/${post.slug}`}
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <div className="blog-card">
                  <img src={post.image} alt={post.title} className="blog-image" />
                  <div className="blog-content">
                    <div className="blog-meta">{post.category}</div>
                    <h3 className="blog-title">{post.title}</h3>
                    <p className="blog-excerpt">{post.excerpt}</p>
                    <div style={{ 
                      marginTop: '16px', 
                      fontSize: '11px', 
                      color: '#6B5744',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span>{post.read_time}</span>
                      <span>→</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="newsletter-box">
            <h3 className="newsletter-title">Stay informed. Stay ahead.</h3>
            <p className="newsletter-desc">Get monthly insights, market updates, and proven STR strategies delivered to your inbox.</p>
            <form className="newsletter-form" onSubmit={handleNewsletterSubmit}>
              <input 
                type="email" 
                placeholder="Your email address"
                className="newsletter-input"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                required
              />
              <button type="submit" className="newsletter-btn">Subscribe</button>
            </form>
          </div>
        </section>

        {/* CTA */}
        <section className="cta-section" id="contact">
          <div className="gold-rule"></div>
          <h2 className="cta-h">Ready to build an STR operation<br/>you're proud of?</h2>
          <p className="cta-sub">Book a free 30-minute discovery call. No pitch, no pressure —<br/>just an honest conversation about where you are and how we can help.</p>
          <a href="mailto:stephanieluxesolutions@gmail.com" className="btn-gold" onClick={(e) => { e.preventDefault(); openCalendly(); }}>Book your free call</a>
          <div className="cta-note">Or email us directly · stephanieluxesolutions@gmail.com</div>
        </section>

        {/* CONTACT FORM */}
        <ContactForm />

        {/* FOOTER */}
        <footer>
          <div className="footer-brand">Stephanies Luxe Solutions FZ LLC · Trade License 46002090</div>
          <div className="footer-copy">© 2026 · All rights reserved · UAE</div>
          <div className="footer-links">
            <a href="#services">Services</a>
            <a href="#about">About</a>
            <a href="#mystery">Mystery Shopping</a>
            <a href="#blog">Blog</a>
            <Link to="/privacy-policy">Privacy Policy</Link>
            <Link to="/terms">Terms & Conditions</Link>
            <a href="#contact">Contact</a>
          </div>
          <div className="footer-social">
            <a href="https://www.instagram.com/stephaniesluxesolutions" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </a>
            <a href="https://www.facebook.com/share/1CgiRDDVCo/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>
          </div>
        </footer>
      </div>
    </>
  );
};

export default Home;